'use client';

import React, { useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { getComparisonDetails } from '../../utils';
import { titleCase } from 'src/utils/titleCase/titleCase';
import { ReimbursementValue } from 'src/components/Profiles/shared/ReimbursementValue';
import { SOC_TYPE_LABELS } from 'src/components/Profiles/components/ProfileDetails/socTypes';

interface ReimbursementRowProps {
  payer: any;
  onResize: ({
    columnId,
    isInnerTable,
  }: {
    columnId: string;
    isInnerTable?: boolean;
  }) => React.CSSProperties;
  isSelected: boolean;
}

const ReimbursementRow = ({
  payer,
  onResize,
  isSelected,
}: ReimbursementRowProps) => {
  const type = titleCase(payer?.payer_type);
  const name = payer?.payer_name;

  const getMetrics = useCallback(
    (item: {
      avg_reimbursement_predicted: number;
      state_avg_reimbursement_predicted: number;
    }) => ({
      ...getComparisonDetails(
        item.avg_reimbursement_predicted,
        item.state_avg_reimbursement_predicted
      ),
    }),
    []
  );

  const { diffArrowIcon, diffArrowColor } = getMetrics(payer);

  // Guard each value on its own field — a payer can have a state average even
  // when it has no provider average of its own (a payer the site doesn't bill).
  const avgReimbursement = payer.avg_reimbursement_predicted ?? 0;
  const stateAvgReimbursement = payer.state_avg_reimbursement_predicted ?? 0;

  const avgReimbursementIsZero = avgReimbursement === 0;
  const stateAvgReimbursementIsZero = stateAvgReimbursement === 0;

  // Drop the row only when there is nothing to show on either field — a payer
  // can have a state average even when it has no provider average of its own.
  const hasNoReimbursementData =
    avgReimbursementIsZero && stateAvgReimbursementIsZero;

  // Show the comparison whenever there is a state average to compare against.
  // When the provider matches the state average the difference renders as $0
  // (rather than blank), so only hide it when there is no state average.
  const hideStateComparison = stateAvgReimbursementIsZero;

  const idName = name?.replace(/ /g, '-')?.toLowerCase();
  if (hasNoReimbursementData) return null;
  return (
    <div
      id={`reimbursement-table-payer-row-${type}-${idName}`}
      key={name}
      data-testid={`reimbursement-table-payer-row-${type}-${idName}`}
      className="flex items-center h-8 relative"
    >
      {isSelected && (
        <div className="absolute inset-0 animate-highlight rounded-md -mx-2 z-0" />
      )}

      <div className="flex gap-10 w-full items-center relative">
        <div
          style={onResize({
            columnId: 'payer',
            isInnerTable: true,
          })}
          className="overflow-hidden"
        >
          <span className="block whitespace-nowrap overflow-hidden text-ellipsis font-normal text-primary">
            {name}
          </span>
        </div>
        <div
          style={onResize({
            columnId: 'siteOfCare',
            isInnerTable: true,
          })}
          className="overflow-hidden"
        >
          <span className="block whitespace-nowrap overflow-hidden text-ellipsis">
            {SOC_TYPE_LABELS[payer.soc_type] || payer.soc_type}
          </span>
        </div>

        <div className="flex-1 text-right font-normal">
          <ReimbursementValue
            amount={payer.avg_reimbursement_predicted}
            format={(value) => `$${value.toLocaleString()}`}
          />
        </div>

        <div className="flex-1 text-right font-normal">
          <ReimbursementValue
            amount={payer.state_avg_reimbursement_predicted}
            format={(value) => `$${value.toLocaleString()}`}
          />
        </div>

        <div className="flex-1 text-right font-normal">
          <span className="flex items-center justify-end">
            {hideStateComparison ? (
              ''
            ) : (
              <>
                <Icon
                  icon={diffArrowIcon}
                  size="sm"
                  className={diffArrowColor}
                />
                $
                {Math.abs(
                  avgReimbursement - stateAvgReimbursement
                ).toLocaleString('en-US')}
              </>
            )}
          </span>
        </div>
        <div className="flex-1 text-right font-normal">
          {payer.claims_total?.toLocaleString('en-US')}
        </div>
      </div>
    </div>
  );
};

export default ReimbursementRow;
